import React, { useState, useEffect, useRef } from 'react'
import '../../SimulateNew/SimulateNew.css' // Reuse styles for consistency
import { useParams, useHistory } from 'react-router'
import { FaRegSave, FaFileImport, FaTrash, FaPlus } from 'react-icons/fa'
import { AiOutlineAudio } from 'react-icons/ai'
import { confirmVoiceoverGeneration } from '../voiceoverCredits'
import { AVAILABLE_MATERIALS } from '../PlaydynamexStandalone/Materials'

const BACKEND_URL = process.env.REACT_APP_API_BASE
const STAGING_URL = `${BACKEND_URL}/api/staging`

const SimulateAdmin = () => {
    // STATE
    const [loading, setLoading] = useState(true)
    const [formData, setFormData] = useState(null)
    const [modelUrl, setModelUrl] = useState('')
    const [selectedFile, setSelectedFile] = useState(null)
    const [previewMode, setPreviewMode] = useState(false)
    const [loadingPreview, setLoadingPreview] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [subprogramTitle, setSubprogramTitle] = useState('')

    // NEW: Animation Selection State
    const [availableAnimations, setAvailableAnimations] = useState([])
    const [showAnimModal, setShowAnimModal] = useState(false)
    const [selectedAnimNames, setSelectedAnimNames] = useState([])

    // PREVIEW STATE
    const [currentStep, setCurrentStep] = useState(1)

    // ADMIN EDITOR STATE (For horizontal navigation of steps)
    const [activeStepIndex, setActiveStepIndex] = useState(0)

    // NEW STATE: TTS Generation tracking
    const [isGeneratingTTS, setIsGeneratingTTS] = useState(false)

    // Refs for hidden JSON file inputs (bulk upload)
    const instructionsInputRef = useRef(null)
    const materialsInputRef = useRef(null)

    const { subId, screenId } = useParams()
    const history = useHistory()

    // Build the API path: include screenId when available
    const apiPath = screenId
        ? `simulate/${subId}/${screenId}`
        : `simulate/${subId}`

    // 1. FETCH STAGING DATA
    useEffect(() => {
        const fetchProject = async () => {
            try {
                const res = await fetch(`${STAGING_URL}/${apiPath}`, {
                    credentials: 'include',
                })
                if (!res.ok) throw new Error('Failed to fetch staging data')
                const data = await res.json()

                setSubprogramTitle(await fetchSubprogramTitle())

                const steps = data.steps || []
                setFormData({
                    title: data.title || '',
                    totalSteps: data.totalSteps || 0,
                    steps,
                })

                if (data.meta?.modelPath) {
                    setModelUrl(data.meta.modelPath)
                }

                // Animation names are extracted server-side at upload time and
                // returned in meta.animations — no GLB download/decode needed.
                // Fall back to names saved on the steps for older projects.
                const savedAnimNames = steps
                    .map((s) => s.animationName)
                    .filter(Boolean)
                const allAnimations =
                    data.meta?.animations?.length > 0
                        ? data.meta.animations
                        : savedAnimNames
                setAvailableAnimations(allAnimations)
                setSelectedAnimNames(
                    savedAnimNames.length > 0 ? savedAnimNames : allAnimations
                )

                setLoading(false)
            } catch (error) {
                console.error('Error fetching admin data:', error)
                setLoading(false)
            }
        }
        fetchProject()
    }, [subId])

    const fetchSubprogramTitle = async () => {
        const BACKEND_URL = process.env.REACT_APP_API_BASE || ''

        if (!subId) return ''

        const res = await fetch(`${BACKEND_URL}/api/program/staging/${(sessionStorage.getItem('allowedDevice') || 'Toki').toLowerCase()}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
        })

        const data = await res.json()
        if (!res.ok) {
            throw new Error(data.error || 'Failed to get program data')
        }

        const matchedSubprogram = (data.subprograms || []).find(
            (s) => s.id === subId
        )

        return matchedSubprogram?.title || ''
    }

    // 2. HANDLE FILE UPLOAD & READ ANIMATIONS
    const handleFileChange = async (e) => {
        const file = e.target.files[0]
        if (!file) return

        const MAX_MODEL_SIZE = 500 * 1024 * 1024 // 50MB (Note: Your comment says 50MB but math is 500MB)
        if (file.size > MAX_MODEL_SIZE) {
            alert('Model file is too large! Maximum allowed size is 500MB.')
            e.target.value = ''
            return
        }

        if (file.name.endsWith('.glb')) {
            const formDataUpload = new FormData()
            formDataUpload.append('model', file)

            setUploading(true)
            
            // --- FRONTEND LOGGING START ---
            const startTime = Date.now();
            console.log(`[UPLOAD] Starting upload for ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)...`);
            // ------------------------------

            try {
                const uploadRes = await fetch(
                    `${BACKEND_URL}/api/upload-simulate-model/${subId}`,
                    {
                        method: 'POST',
                        body: formDataUpload,
                    }
                )

                if (!uploadRes.ok) {
                    const errorText = await uploadRes.text();
                    throw new Error(`Server returned ${uploadRes.status}: ${errorText}`);
                }

                const uploadData = await uploadRes.json()
                
                // --- FRONTEND LOGGING SUCCESS ---
                const duration = ((Date.now() - startTime) / 1000).toFixed(2);
                console.log(`[UPLOAD] Upload successful! Took ${duration} seconds.`);
                // --------------------------------

                setModelUrl(uploadData.modelUrl)

                // Animation names are extracted server-side and returned in the
                // response, so the selection modal opens instantly with no
                // browser-side model download or decode.
                const animations = uploadData.animations || []
                setAvailableAnimations(animations)

                if (animations.length > 0) {
                    setSelectedAnimNames(animations)
                    setShowAnimModal(true)
                    alert('Model uploaded successfully!')
                } else {
                    alert(
                        'Model uploaded, but no animations were found in the GLB file.'
                    )
                }
            } catch (err) {
                // --- FRONTEND LOGGING ERROR ---
                const duration = ((Date.now() - startTime) / 1000).toFixed(2);
                console.error(`[UPLOAD] Upload failed after ${duration} seconds:`, err);
                // ------------------------------
                alert(`Failed to upload the model. Check console for details.`);
            } finally {
                setUploading(false)
            }
        } else {
            alert('Please select a valid .glb file.')
        }

        e.target.value = ''
    }

    // 3. HANDLE EDITS
    const handleTitleChange = (e) => {
        setFormData((prev) => ({ ...prev, title: e.target.value }))
        // Do nothing for now. Later it will be used for the tutorial (for users on top side)
    }

    const handleStepChange = (index, field, value) => {
        const updatedSteps = [...formData.steps]
        updatedSteps[index] = { ...updatedSteps[index], [field]: value }
        setFormData((prev) => ({ ...prev, steps: updatedSteps }))
    }

    // --- MATERIALS EDITOR HANDLERS ---
    const addMaterial = (index) => {
        const updatedSteps = [...formData.steps]
        const currentMaterials = { ...(updatedSteps[index].materials || {}) }

        // Find the first material from the available list that isn't already assigned
        const defaultKey =
            AVAILABLE_MATERIALS.find((m) => !currentMaterials[m]) ||
            AVAILABLE_MATERIALS[0]

        currentMaterials[defaultKey] = 1
        updatedSteps[index] = {
            ...updatedSteps[index],
            materials: currentMaterials,
        }
        setFormData((prev) => ({ ...prev, steps: updatedSteps }))
    }

    const updateMaterial = (index, oldKey, newKey, newValue) => {
        const updatedSteps = [...formData.steps]
        const currentMaterials = { ...(updatedSteps[index].materials || {}) }

        // If the dropdown key was changed, delete the old key
        if (oldKey !== newKey) {
            delete currentMaterials[oldKey]
        }

        currentMaterials[newKey] = Math.max(1, Number(newValue)) // Minimum value of 1
        updatedSteps[index] = {
            ...updatedSteps[index],
            materials: currentMaterials,
        }
        setFormData((prev) => ({ ...prev, steps: updatedSteps }))
    }

    const removeMaterial = (index, key) => {
        const updatedSteps = [...formData.steps]
        const currentMaterials = { ...(updatedSteps[index].materials || {}) }

        delete currentMaterials[key]
        updatedSteps[index] = {
            ...updatedSteps[index],
            materials: currentMaterials,
        }
        setFormData((prev) => ({ ...prev, steps: updatedSteps }))
    }

    // --- Bulk JSON Upload Parser (auto-saves to staging) ---
    const processJsonUpload = (e, type) => {
        const file = e.target.files[0]
        if (!file) return

        const reader = new FileReader()
        reader.onload = async (event) => {
            try {
                const parsed = JSON.parse(event.target.result)
                let newSteps = [...(formData?.steps || [])]

                const maxStep = Math.max(...Object.keys(parsed).map(Number))

                for (let i = 0; i < maxStep; i++) {
                    if (!newSteps[i]) {
                        newSteps[i] = {
                            stepNumber: i + 1,
                            animationName: String(i + 1).padStart(2, '0'),
                            title: '',
                            instruction: '',
                            materials: {},
                            speed: 1.0,
                        }
                    }
                }

                Object.entries(parsed).forEach(([key, val]) => {
                    const idx = Number(key) - 1
                    if (newSteps[idx]) {
                        if (type === 'instructions') {
                            newSteps[idx].instruction = val.text || ''
                        } else if (type === 'materials') {
                            newSteps[idx].materials = val || {}
                        }
                    }
                })

                const updatedData = {
                    ...formData,
                    steps: newSteps,
                    totalSteps: Math.max(
                        formData?.totalSteps || 0,
                        newSteps.length
                    ),
                }

                setFormData(updatedData)

                const res = await fetch(`${STAGING_URL}/${apiPath}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(updatedData),
                    credentials: 'include',
                })

                if (!res.ok) throw new Error('Failed to save to database')

                alert(
                    `✅ ${type === 'instructions' ? 'Instructions' : 'Materials'} JSON parsed and auto-saved to staging successfully!`
                )
            } catch (err) {
                console.error(err)
                alert(
                    `❌ Error parsing or saving ${type} JSON. Please check the file format.`
                )
            }
        }
        reader.readAsText(file)
        e.target.value = ''
    }

    // 4. SAVE TO STAGING
    // 4. SAVE TUTORIAL STEPS TO STAGING
    const handleSave = async () => {
        try {
            // We only need to save the tutorial steps/text.
            // The modelPath was already updated in project_map.json during the file upload.
            const res = await fetch(`${STAGING_URL}/${apiPath}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
                credentials: 'include',
            })

            if (!res.ok) throw new Error('Failed to save JSON data')

            alert('Tutorial steps saved successfully to staging!')
        } catch (error) {
            console.error('Save error:', error)
            alert('Failed to save tutorial steps.')
        }
    }

    const handleGenerateVoiceover = async () => {
        // Remind them to save first, because the backend reads from the JSON file
        // Single confirmation gate: save reminder + estimated credit cost.
        if (!confirmVoiceoverGeneration(formData)) return

        // Fallback to 'simulate1' if dealing with older legacy routes
        const currentScreenId = screenId || 'simulate1'

        setIsGeneratingTTS(true)
        try {
            // --- READ FROM LOCAL STORAGE ---
            const apiKey = localStorage.getItem('freepikApiKey')
            const voiceId = localStorage.getItem('elevenVoiceId')
            if (!apiKey || !voiceId) {
                window.alert(
                    'Missing TTS Configuration!\n\nPlease go back to the main Toki Engine Admin page and set the ElevenLabs API Key and Voice ID before generating voiceovers.'
                )
                setIsGeneratingTTS(false)
                return
            }

            const res = await fetch(
                `${BACKEND_URL}/api/project/${subId}/generate-tts/simulate/${currentScreenId}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' }, // IMPORTANT: Add this header
                    body: JSON.stringify({ apiKey, voiceId }), // IMPORTANT: Send the keys
                }
            )
            const data = await res.json()

            if (!res.ok) {
                throw new Error(data.error || 'Failed to generate voiceover')
            }

            alert(`✅ ${data.message}`)
        } catch (error) {
            console.error('TTS Generation error:', error)
            alert(`❌ Error generating voiceover: ${error.message}`)
        } finally {
            setIsGeneratingTTS(false)
        }
    }

    const handlePreviewClick = async () => {
        if (
            !window.confirm(
                'Have you saved all your changes? Unsaved changes will be lost. Click OK to continue and preview the last saved version.'
            )
        )
            return
        if (!modelUrl) {
            alert('No model uploaded. Please upload a GLB model first.')
            // return
        }

        try {
            setLoadingPreview(true)
            await new Promise((resolve) => setTimeout(resolve, 1000))

            setLoadingPreview(false)

            const res = await fetch(modelUrl, {
                method: 'HEAD',
                mode: 'cors',
            })

            if (!res.ok) {
                alert(
                    'Model file not found on server. Please upload the model again.'
                )
                return
            }

            history.push(
                `/simulateNew?preview=true&projectId=${subId}${screenId ? `&screenId=${screenId}` : ''}`
            )
        } catch (err) {
            console.error(err)
            alert('Unable to verify model file. Please check the upload.')
        }
    }

    // ==========================================
    // RENDER ADMIN PANEL
    // ==========================================
    if (loading)
        return <div style={{ padding: '20px' }}>Loading Admin Panel...</div>
    if (!formData)
        return (
            <div style={{ padding: '20px' }}>
                Error loading data. Try logging in again.
            </div>
        )

    const activeStepData = formData.steps[activeStepIndex]

    const toggleAnimationSelection = (animName) => {
        setSelectedAnimNames((prev) =>
            prev.includes(animName)
                ? prev.filter((name) => name !== animName)
                : [...prev, animName]
        )
    }

    const confirmAnimationSelection = () => {
        const sortedSelection = availableAnimations.filter((anim) =>
            selectedAnimNames.includes(anim)
        )

        setFormData((prev) => {
            const updatedSteps = sortedSelection.map((animName, index) => {
                const existingStep =
                    prev.steps.find((s) => s.animationName === animName) ||
                    prev.steps[index]
                return {
                    stepNumber: index + 1,
                    animationName: animName, // SAVES THE ACTUAL NAME
                    title: existingStep?.title || '',
                    instruction: existingStep?.instruction || '',
                    materials: existingStep?.materials || {},
                    // Add the speed property, default to 1.0
                    speed:
                        existingStep?.speed !== undefined
                            ? existingStep.speed
                            : 1.0,
                }
            })
            return {
                ...prev,
                totalSteps: updatedSteps.length,
                steps: updatedSteps,
            }
        })

        setActiveStepIndex(0)
        setShowAnimModal(false)
    }

    return (
        <div
            style={{
                padding: '20px 40px',
                maxWidth: '900px',
                margin: '0 auto',
                fontFamily: 'sans-serif',
                height: '100vh',
                boxSizing: 'border-box',
                overflowY: 'auto',
            }}
        >
            {showAnimModal && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                    }}
                >
                    <div
                        style={{
                            background: '#fff',
                            padding: '30px',
                            borderRadius: '8px',
                            width: '400px',
                            maxHeight: '80vh',
                            overflowY: 'auto',
                        }}
                    >
                        <h2>Select Animations</h2>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '10px',
                                margin: '20px 0',
                            }}
                        >
                            {availableAnimations.map((anim, idx) => (
                                <label
                                    key={idx}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        padding: '8px',
                                        background: '#f9f9f9',
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedAnimNames.includes(
                                            anim
                                        )}
                                        onChange={() =>
                                            toggleAnimationSelection(anim)
                                        }
                                        style={{ marginRight: '10px' }}
                                    />
                                    {idx + 1}. {anim}
                                </label>
                            ))}
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '10px',
                            }}
                        >
                            <button onClick={() => setShowAnimModal(false)}>
                                Cancel
                            </button>
                            <button
                                onClick={confirmAnimationSelection}
                                style={{
                                    background: '#1a73e8',
                                    color: '#fff',
                                    padding: '8px 16px',
                                }}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Subprogram heading row */}
            <div
                style={{
                    marginBottom: '12px',
                    padding: '10px 14px',
                    background:
                        'linear-gradient(90deg, #eef4ff 0%, #f8fbff 100%)',
                    border: '1px solid #dbe7ff',
                    borderRadius: '10px',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <div
                        style={{
                            fontSize: '0.9rem',
                            fontWeight: 700,
                            color: '#5b6b8c',
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                        }}
                    >
                        Current Project
                    </div>

                    <h1
                        style={{
                            margin: 0,
                            fontSize: '1.65rem',
                            fontWeight: 800,
                            color: '#1f3b77',
                            lineHeight: 1.2,
                        }}
                    >
                        {subprogramTitle}
                    </h1>
                </div>
            </div>

            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px',
                }}
            >
                <h1 style={{ margin: 0, fontSize: '1.8rem' }}>
                    Simulate Editor
                </h1>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                        onClick={() => setShowAnimModal(true)}
                        style={{
                            padding: '10px 20px',
                            background: '#333',
                            color: 'white',
                            borderRadius: '6px',
                        }}
                    >
                        Manage Steps
                    </button>
                    <button
                        onClick={handleGenerateVoiceover}
                        disabled={isGeneratingTTS}
                        style={{
                            padding: '10px 20px',
                            background: isGeneratingTTS ? '#9e9e9e' : '#ff9800', // Orange color for audio tools
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: isGeneratingTTS ? 'not-allowed' : 'pointer',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                        }}
                    >
                        {isGeneratingTTS ? (
                            'Generating...'
                        ) : (
                            <>
                                <AiOutlineAudio
                                    style={{
                                        fontSize: '22px',
                                        marginRight: '10px',
                                    }}
                                />{' '}
                                Generate Voiceover
                            </>
                        )}
                    </button>
                    <button
                        onClick={handlePreviewClick}
                        disabled={!modelUrl}
                        style={{
                            padding: '10px 20px',
                            background: '#6200ea',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                        }}
                    >
                        {loadingPreview ? 'Loading...' : 'Preview'}
                    </button>
                    <button
                        onClick={handleSave}
                        style={{
                            padding: '10px 20px',
                            background: '#1a73e8',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                        }}
                    >
                        <FaRegSave
                            style={{
                                fontSize: '22px',
                                marginRight: '10px',
                            }}
                        />
                        Save
                    </button>
                </div>
            </div>

            {/* General Settings */}
            <div
                style={{
                    background: '#f4f4f9',
                    padding: '20px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    border: '1px solid #e0e0e0',
                }}
            >
                <div style={{ display: 'flex', gap: '20px' }}>
                    <div style={{ flex: 1 }}>
                        <label
                            style={{
                                display: 'block',
                                fontWeight: 'bold',
                                marginBottom: '5px',
                            }}
                        >
                            Tutorial Title
                        </label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={handleTitleChange}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '4px',
                                border: '1px solid #ccc',
                                boxSizing: 'border-box',
                            }}
                        />
                    </div>
                    <div style={{ flex: 1 }}>
                        <label
                            style={{
                                display: 'block',
                                fontWeight: 'bold',
                                marginBottom: '5px',
                            }}
                        >
                            Upload New GLB Model
                        </label>
                        <input
                            type="file"
                            accept=".glb"
                            onChange={handleFileChange}
                            disabled={uploading}
                            style={{ display: 'block', padding: '6px 0' }}
                        />
                        {modelUrl && !uploading && (
                            <p
                                style={{
                                    color: '#2e7d32',
                                    fontWeight: 'bold',
                                    margin: '8px 0 0',
                                    fontSize: '0.9rem',
                                }}
                            >
                                ✓ A model is currently linked to this Simulate
                                screen.
                            </p>
                        )}
                        {uploading && (
                            <p
                                style={{
                                    color: '#1976d2',
                                    fontWeight: 'bold',
                                    margin: '8px 0 0',
                                }}
                            >
                                Uploading model to cloud storage...
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Steps Editor - Horizontal Single Step View */}
            <div>
                <h2 style={{ marginBottom: '10px' }}>
                    Step Instructions Editor
                </h2>
                {formData.totalSteps === 0 ? (
                    <p style={{ color: '#d32f2f', fontWeight: 'bold' }}>
                        No steps detected. Please ensure your GLB has
                        animations.
                    </p>
                ) : (
                    <div
                        style={{
                            border: '1px solid #ddd',
                            padding: '20px',
                            borderRadius: '8px',
                            background: '#fff',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        }}
                    >
                        {/* Pagination Header */}
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '20px',
                                paddingBottom: '15px',
                                borderBottom: '1px solid #eee',
                            }}
                        >
                            <h3 style={{ margin: 0, color: '#6200ea' }}>
                                Step {activeStepData?.stepNumber}{' '}
                                <span
                                    style={{
                                        color: '#888',
                                        fontSize: '1rem',
                                        fontWeight: 'normal',
                                    }}
                                >
                                    of {formData.totalSteps}
                                </span>
                                <span
                                    style={{
                                        marginLeft: '15px',
                                        fontSize: '0.9rem',
                                        color: '#1a73e8',
                                    }}
                                >
                                    Animation Name:{' '}
                                    {activeStepData?.animationName || ''}
                                </span>
                            </h3>

                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    onClick={() =>
                                        setActiveStepIndex((prev) =>
                                            Math.max(0, prev - 1)
                                        )
                                    }
                                    disabled={activeStepIndex === 0}
                                    style={{
                                        padding: '8px 16px',
                                        background:
                                            activeStepIndex === 0
                                                ? '#f0f0f0'
                                                : '#fff',
                                        border: '1px solid #ccc',
                                        borderRadius: '6px',
                                        cursor:
                                            activeStepIndex === 0
                                                ? 'not-allowed'
                                                : 'pointer',
                                    }}
                                >
                                    &larr; Previous
                                </button>
                                <button
                                    onClick={() =>
                                        setActiveStepIndex((prev) =>
                                            Math.min(
                                                formData.totalSteps - 1,
                                                prev + 1
                                            )
                                        )
                                    }
                                    disabled={
                                        activeStepIndex ===
                                        formData.totalSteps - 1
                                    }
                                    style={{
                                        padding: '8px 16px',
                                        background:
                                            activeStepIndex ===
                                            formData.totalSteps - 1
                                                ? '#f0f0f0'
                                                : '#fff',
                                        border: '1px solid #ccc',
                                        borderRadius: '6px',
                                        cursor:
                                            activeStepIndex ===
                                            formData.totalSteps - 1
                                                ? 'not-allowed'
                                                : 'pointer',
                                    }}
                                >
                                    Next &rarr;
                                </button>
                            </div>
                        </div>

                        {/* Editor Fields for Active Step */}
                        <div style={{ marginBottom: '15px' }}>
                            <label
                                style={{
                                    display: 'block',
                                    fontWeight: 'bold',
                                    marginBottom: '5px',
                                }}
                            >
                                Step Title
                            </label>
                            <input
                                type="text"
                                value={activeStepData?.title || ''}
                                onChange={(e) =>
                                    handleStepChange(
                                        activeStepIndex,
                                        'title',
                                        e.target.value
                                    )
                                }
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '4px',
                                    border: '1px solid #ccc',
                                    boxSizing: 'border-box',
                                }}
                                placeholder="e.g. Add the Side Walls"
                            />
                        </div>

                        <div>
                            <label
                                style={{
                                    display: 'block',
                                    fontWeight: 'bold',
                                    marginBottom: '5px',
                                }}
                            >
                                Instruction Text
                            </label>
                            <textarea
                                value={activeStepData?.instruction || ''}
                                onChange={(e) =>
                                    handleStepChange(
                                        activeStepIndex,
                                        'instruction',
                                        e.target.value
                                    )
                                }
                                rows="5"
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '4px',
                                    border: '1px solid #ccc',
                                    resize: 'none',
                                    boxSizing: 'border-box',
                                    fontFamily: 'inherit',
                                }}
                                placeholder="Describe what needs to be done in this step..."
                            />
                        </div>

                        {/* --- ANIMATION SPEED SLIDER --- */}
                        <div style={{ marginTop: '15px' }}>
                            <label
                                style={{
                                    display: 'block',
                                    fontWeight: 'bold',
                                    marginBottom: '5px',
                                }}
                            >
                                Animation Speed:{' '}
                                {activeStepData?.speed !== undefined
                                    ? activeStepData.speed
                                    : 1.0}
                                x
                            </label>
                            <input
                                type="range"
                                min="0.1"
                                max="2.0"
                                step="0.1"
                                value={
                                    activeStepData?.speed !== undefined
                                        ? activeStepData.speed
                                        : 1.0
                                }
                                onChange={(e) =>
                                    handleStepChange(
                                        activeStepIndex,
                                        'speed',
                                        parseFloat(e.target.value)
                                    )
                                }
                                style={{ width: '100%', cursor: 'pointer' }}
                            />
                            <small style={{ color: '#666' }}>
                                Speed ranges from 0.1x (slow motion) to 2.0x
                                (double speed). Default is 1.0x.
                            </small>
                        </div>

                        {/* --- MATERIALS EDITOR UI --- */}
                        <div
                            style={{
                                marginTop: '20px',
                                paddingTop: '15px',
                                borderTop: '1px dashed #ccc',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '10px',
                                }}
                            >
                                <label
                                    style={{
                                        display: 'block',
                                        fontWeight: 'bold',
                                        margin: 0,
                                    }}
                                >
                                    Step Materials
                                </label>
                                <button
                                    onClick={() => addMaterial(activeStepIndex)}
                                    style={{
                                        padding: '6px 12px',
                                        background: '#f0f0f0',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                        fontSize: '13px',
                                    }}
                                >
                                    <FaPlus size={10} /> Add Material
                                </button>
                            </div>

                            {!activeStepData?.materials ||
                            Object.keys(activeStepData.materials).length ===
                                0 ? (
                                <p
                                    style={{
                                        color: '#888',
                                        fontSize: '13px',
                                        margin: 0,
                                        fontStyle: 'italic',
                                    }}
                                >
                                    No materials assigned for this step.
                                </p>
                            ) : (
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '10px',
                                    }}
                                >
                                    {Object.entries(
                                        activeStepData.materials
                                    ).map(([matKey, matVal]) => (
                                        <div
                                            key={matKey}
                                            style={{
                                                display: 'flex',
                                                gap: '10px',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <select
                                                value={matKey}
                                                onChange={(e) =>
                                                    updateMaterial(
                                                        activeStepIndex,
                                                        matKey,
                                                        e.target.value,
                                                        matVal
                                                    )
                                                }
                                                style={{
                                                    flex: 1,
                                                    padding: '8px',
                                                    borderRadius: '4px',
                                                    border: '1px solid #ccc',
                                                }}
                                            >
                                                <option disabled value="">
                                                    -- Select Material --
                                                </option>
                                                {AVAILABLE_MATERIALS.map(
                                                    (m) => (
                                                        <option
                                                            key={m}
                                                            value={m}
                                                            disabled={
                                                                m !== matKey &&
                                                                activeStepData
                                                                    .materials[
                                                                    m
                                                                ] !== undefined
                                                            }
                                                        >
                                                            {m.replace(
                                                                /_/g,
                                                                ' '
                                                            )}
                                                        </option>
                                                    )
                                                )}
                                            </select>

                                            <input
                                                type="number"
                                                min="1"
                                                value={matVal}
                                                onChange={(e) =>
                                                    updateMaterial(
                                                        activeStepIndex,
                                                        matKey,
                                                        matKey,
                                                        e.target.value
                                                    )
                                                }
                                                style={{
                                                    width: '80px',
                                                    padding: '8px',
                                                    borderRadius: '4px',
                                                    border: '1px solid #ccc',
                                                    textAlign: 'center',
                                                }}
                                            />

                                            <button
                                                onClick={() =>
                                                    removeMaterial(
                                                        activeStepIndex,
                                                        matKey
                                                    )
                                                }
                                                style={{
                                                    background: '#ffebec',
                                                    color: '#d32f2f',
                                                    border: '1px solid #ffcdd2',
                                                    borderRadius: '4px',
                                                    padding: '8px 12px',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                                title="Remove Material"
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* --- Bulk JSON Uploaders --- */}
            <div
                style={{
                    marginTop: '30px',
                    padding: '20px',
                    background: '#fff',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                }}
            >
                <h3 style={{ marginTop: 0, color: '#1f3b77' }}>
                    Bulk Upload Project Data
                </h3>
                <p
                    style={{
                        color: '#666',
                        fontSize: '14px',
                        marginBottom: '15px',
                    }}
                >
                    Upload pre-formatted JSON files to instantly populate
                    instructions and material lists. Changes will auto-save to
                    staging immediately.
                </p>
                <div style={{ display: 'flex', gap: '15px' }}>
                    <button
                        onClick={() => instructionsInputRef.current.click()}
                        style={{
                            padding: '10px 16px',
                            background: '#eef4ff',
                            color: '#1a73e8',
                            border: '1px solid #1a73e8',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}
                    >
                        <FaFileImport /> Upload Instructions JSON
                    </button>
                    <input
                        type="file"
                        accept=".json"
                        ref={instructionsInputRef}
                        style={{ display: 'none' }}
                        onChange={(e) => processJsonUpload(e, 'instructions')}
                    />

                    <button
                        onClick={() => materialsInputRef.current.click()}
                        style={{
                            padding: '10px 16px',
                            background: '#f8ebff',
                            color: '#6200ea',
                            border: '1px solid #6200ea',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}
                    >
                        <FaFileImport /> Upload Materials JSON
                    </button>
                    <input
                        type="file"
                        accept=".json"
                        ref={materialsInputRef}
                        style={{ display: 'none' }}
                        onChange={(e) => processJsonUpload(e, 'materials')}
                    />
                </div>
            </div>
        </div>
    )
}

export default SimulateAdmin
